import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { company } from "../data/mock";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill required fields");
      return;
    }
    const existing = JSON.parse(localStorage.getItem("fss_contacts") || "[]");
    existing.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem("fss_contacts", JSON.stringify(existing));
    setSent(true);
    toast.success("Message sent successfully!");
    setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    setTimeout(() => setSent(false), 4000);
  };

  return (
    <main>
      <section className="brand-navy text-white py-14">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <p className="text-sm uppercase tracking-[0.3em] text-brand-red font-semibold">Get in Touch</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold mt-2">Contact Us</h1>
          <nav className="text-sm text-white/60 mt-3">
            <Link to="/" className="hover:text-white">Home</Link> / <span className="text-white">Contact Us</span>
          </nav>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          {[
            { i: MapPin, t: "Address", v: company.address },
            { i: Phone, t: "Phone", v: company.phone, link: `tel:${company.phoneRaw}` },
            { i: Mail, t: "Email", v: company.email, link: `mailto:${company.email}` },
          ].map(({ i: Icon, t, v, link }, idx) => (
            <div key={idx} className="bg-white border border-gray-100 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-md bg-[#f5f6fb] flex items-center justify-center mb-3">
                <Icon size={22} className="text-brand-red" />
              </div>
              <h3 className="text-lg font-bold text-brand-navy mb-1">{t}</h3>
              {link ? <a href={link} className="text-gray-600 text-sm hover:text-brand-red">{v}</a> : <p className="text-gray-600 text-sm leading-relaxed">{v}</p>}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="bg-white border border-gray-100 rounded-lg p-8 shadow-sm">
            <h2 className="font-display text-2xl font-bold text-brand-navy mb-2">Send Us A Message</h2>
            <p className="text-sm text-gray-500 mb-6">We typically respond within 24 hours.</p>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your Name *" required />
                <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="Email *" required />
                <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="Phone" />
                <Input value={form.subject} onChange={(e) => update("subject", e.target.value)} placeholder="Subject" />
              </div>
              <Textarea value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Your Message *" rows={5} required />
              <Button type="submit" className="btn-red-brand w-full md:w-auto px-7 py-3 h-auto text-sm font-semibold">
                <Send size={15} className="mr-2" /> Send Message
              </Button>
              {sent && <p className="text-green-600 text-sm">✓ Your message has been sent!</p>}
            </form>
          </div>

          <div>
            <h2 className="font-display text-2xl font-bold text-brand-navy mb-2">Working Hours</h2>
            <p className="text-gray-500 text-sm mb-5">Visit our facility or reach out to us during business hours.</p>
            <div className="bg-white border border-gray-100 rounded-lg p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <Clock size={22} className="text-brand-red" />
                <h3 className="text-lg font-bold text-brand-navy">Office Hours</h3>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex justify-between"><span className="text-gray-600">Monday - Friday</span><span className="font-semibold text-brand-navy">9:00 AM - 6:00 PM</span></li>
                <li className="flex justify-between"><span className="text-gray-600">Saturday</span><span className="font-semibold text-brand-navy">9:00 AM - 4:00 PM</span></li>
                <li className="flex justify-between"><span className="text-gray-600">Sunday</span><span className="font-semibold text-brand-red">Closed</span></li>
              </ul>
            </div>
            <div className="rounded-lg overflow-hidden border border-gray-100 shadow-sm">
              <iframe
                title="map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14730.012!2d73.4732!3d22.5092!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zSGFsb2wsIEd1amFyYXQ!5e0!3m2!1sen!2sin!4v1700000000"
                width="100%" height="280" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
